import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import getImageStyle from 'utils/getImageStyle';
import fullname from 'utils/fullname';
import withProfile from 'hocs/withProfile';

import PublicationRowLock from 'admin-editor/containers/PublicationRowLock';

import Avatar from 'components/Avatar';
import TrafficLabel from 'admin-editor/components/TrafficLabel';

import SecondaryButton from 'styled/Buttons/SecondaryButton';

import List from 'icons/List';
import DefaultImage from 'icons/DefaultImage';
import BrokenImage from 'icons/BrokenImage';
import { ReactComponent as Time } from 'icons/Time.svg';

import Preview from './Preview';
import PreviewContainer from './PreviewContainer';
import Title from './Title';

import RightColumn from './RightColumn';
import AuthorContainer from './AuthorContainer';
import StatesContainer from './StatesContainer';
import StatesDropdown from './StatesDropdown';
import StatesItemLabel from './StatesItemLabel';
import Author from './Author';
import Container from './Container';
import Default from './Default';
import StyledLink from './StyledLink';
import PublicationItem from './PublicationItem';

const STATES = {
  draft: 'Черновик',
  published: 'Опубликовано',
  processing: 'В обработке',
  hidden: 'Скрыта',
};

const formattedDate = (date) => {
  if (!date) return '';

  return (
    new Date(date).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Moscow',
    })
  );
};

export class PublicationRow extends React.PureComponent {
  state = {
    isPreviewShow: false,
  }

  componentDidMount() {
    if (!this.src) return;

    const image = new Image();
    image.onload = () => this.setState({ isPreviewShow: true });
    image.src = this.src;
  }

  get src() {
    if (!this.props.mainImage) return this.props.mainImage;

    return getImageStyle(this.props.mainImage, '80x80');
  }

  get publicationLocked() {
    return this.props.locked && this.props.lockedEmployeeId !== this.props.profile.id;
  }

  render() {
    return (
      <PublicationItem>
        <PreviewContainer
          as={this.publicationLocked ? 'div' : Link}
          to={`/publications/${this.props.id}`}
        >
          {!this.state.isPreviewShow && (
            <Default>
              {!this.props.mainImage ? <DefaultImage /> : <BrokenImage />}
            </Default>
          )}

          {this.state.isPreviewShow && (
            <Preview
              src={this.src}
              alt={this.props.title}
            />
          )}
        </PreviewContainer>

        <div style={{ paddingLeft: '15px' }}>
          <Title as={this.publicationLocked ? 'h1' : Link} to={`/publications/${this.props.id}`}>
            {this.props.title}
          </Title>

          {this.props.link &&
            <StyledLink href={this.props.link} rel="nofollow noopener">{this.props.link}</StyledLink>
          }

          <Container>
            <AuthorContainer>
              {this.props.author && (
                <React.Fragment>
                  <Avatar
                    size="32px"
                    fontSize="13px"
                    url={this.props.author.avatar && this.props.author.avatar.url}
                    name={fullname(this.props.author)}
                  />

                  <Author>{fullname(this.props.author)}</Author>
                </React.Fragment>
              )}
            </AuthorContainer>

            <div style={{ position: 'relative', alignSelf: 'center' }}>
              <StatesContainer>
                <div style={{ marginRight: '8px' }}><Time /></div>

                {formattedDate(this.props.createdAt)}
              </StatesContainer>

              <StatesDropdown>
                <div style={{ fontSize: '16px' }}>Дата</div>

                <div>
                  <StatesItemLabel>Создания</StatesItemLabel>

                  {formattedDate(this.props.createdAt)}
                </div>

                {this.props.updatedAt > 0 && (
                  <div>
                    <StatesItemLabel>Изменения</StatesItemLabel>

                    {formattedDate(this.props.updatedAt)}
                  </div>
                )}

                {this.props.publishedAt > 0 && this.props.state === 'published' && (
                  <div>
                    <StatesItemLabel>Публикации</StatesItemLabel>

                    {formattedDate(this.props.publishedAt)}
                  </div>
                )}
              </StatesDropdown>
            </div>

            <StatesContainer style={{ width: '140px', justifyContent: 'flex-start' }}>
              <div style={{ marginRight: '5px' }}><List /></div>

              {STATES[this.props.state]}
            </StatesContainer>

            {this.publicationLocked && (
              <PublicationRowLock
                publicationId={this.props.id}
                lockedEmployeeFullname={this.props.lockedEmployeeFullname}
              />
            )}
          </Container>
        </div>

        <RightColumn>
          <SecondaryButton
            as={Link}
            to={`/publications/${this.props.id}`}
            style={{ textDecoration: 'none' }}
            disabled={this.publicationLocked}
          >
            Редактировать
          </SecondaryButton>

          <div style={{ marginTop: '25px' }}>
            <TrafficLabel
              isEditorApproved={this.props.isEditorApproved}
              isCorrectorApproved={this.props.isCorrectorApproved}
            />
          </div>
        </RightColumn>
      </PublicationItem>
    );
  }
}

PublicationRow.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  state: PropTypes.oneOf(['draft', 'published', 'processing', 'hidden']),

  isEditorApproved: PropTypes.bool,
  isCorrectorApproved: PropTypes.bool,

  author: PropTypes.shape({
    avatar: PropTypes.shape({ url: PropTypes.string }),
  }),
  mainImage: PropTypes.string,
  updatedAt: PropTypes.number,
  publishedAt: PropTypes.number,
  createdAt: PropTypes.number.isRequired,
  link: PropTypes.string,

  locked: PropTypes.bool,
  lockedEmployeeFullname: PropTypes.string,
  lockedEmployeeId: PropTypes.string,
  profile: PropTypes.shape({ id: PropTypes.string }),
};

PublicationRow.defaultProps = {
  title: '',
  state: 'draft',

  isEditorApproved: false,
  isCorrectorApproved: false,

  author: undefined,
  mainImage: '',
  updatedAt: undefined,
  publishedAt: undefined,
  link: '',
  locked: false,
  lockedEmployeeFullname: undefined,
  lockedEmployeeId: undefined,
  profile: {},
};

export default withProfile(PublicationRow);
